/**
 * @congress/auth-helper v1.0.0
 * @version 1.0.0
 * @license CYIM: www.cyim.com
 */

import AuthType from './AuthType.enum.js';
class DrupalService {
  /**
   * Default constructor
   * @constructor DrupalService
   * @param drupalURI - Default "/user/oauth/access_token?renew=1"
   */
  constructor() {
    let drupalURI = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "/user/oauth/access_token?renew=1";
    this.type = AuthType.DRUPAL;
    this._drupalURI = drupalURI;
    this._token = null;
    this._tokenParsed = null;
  }

  /**
   * Decode JWT Token
   * @param str
   * @returns {null | string}
   * @private
   */
  _decodeToken(str) {
    let token = null;
    try {
      if (str) {
        str = str.split(".")[1];
        str = str.replace(/-/g, "+");
        str = str.replace(/_/g, "/");
        switch (str.length % 4) {
          case 0:
            break;
          case 2:
            str += "==";
            break;
          case 3:
            str += "=";
            break;
          default:
            throw new Error("Invalid token");
        }
        str = decodeURIComponent(escape(window.atob(str)));
        token = JSON.parse(str);
      }
    } catch (err) {
      console.error("Failed to decode token:", err);
    }
    return token;
  }

  /**
   * Set token if toke is valid & not expired
   * @param token
   * @returns {boolean}
   * @private
   */
  _setToken(token) {
    let tokenParsed = this._decodeToken(token);
    if (tokenParsed) {
      this._token = token;
      this._tokenParsed = tokenParsed;
      return true;
    }
    return false;
  }

  /**
   * Is true if the user is authenticated, false otherwise.
   * @returns {boolean}
   */
  isAuthenticated() {
    return !!this._tokenParsed && !this.isTokenExpired();
  }

  /**
   * Returns true if the token has less than minValidity seconds left before it expires (minValidity is optional, if not specified 0 is used).
   * @param {number} minValidity - duration in seconds
   * @returns {boolean}
   */
  isTokenExpired() {
    let minValidity = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    if (!this._tokenParsed || !this._tokenParsed?.exp) {
      return true;
    }
    let expiresIn = this._tokenParsed.exp - Math.ceil(new Date().getTime() / 1000);
    if (minValidity) {
      if (isNaN(minValidity)) {
        throw 'Invalid minValidity';
      }
      expiresIn -= minValidity;
    }
    return expiresIn < 0;
  }

  /**
   * Fetch & set token from drupal or set default one
   * @returns {Promise<string|null>}
   * @private
   */
  async _getTokenFromDrupal() {
    try {
      // Module Drupal : https://git.srvteam.com/Drupal/drupal-9/modules/oauth_token/-/tree/master
      const tokenFromDrupal = await fetch(this._drupalURI).then(r => r.json());
      if (tokenFromDrupal?.access_token) {
        return tokenFromDrupal.access_token;
      }
      const localStorageToken = localStorage.getItem("keycloak-token");
      if (localStorageToken) {
        return localStorageToken;
      }
    } catch (e) {
      console.error(`[Error] Failed to get new token ${e}`);
    }
    return null;
  }

  /**
   * Check if authenticated otherwise get a new token from drupal
   * @returns {Promise<void>}
   * @private
   */
  async _checkToken() {
    if (!this.isAuthenticated()) {
      let tokenDrupal = await this._getTokenFromDrupal();
      if (tokenDrupal) {
        this._setToken(tokenDrupal);
      } else {
        this._token = null;
        this._tokenParsed = null;
      }
    }
  }

  /**
   * The base64 encoded token that can be sent in the Authorization header in requests to services.
   * @returns {Promise<void>}
   */
  async getToken() {
    return this._checkToken().then(() => {
      return this._token;
    });
  }

  /**
   * The parsed token as a JavaScript object.
   * @returns {Promise<void>}
   */
  async getTokenParsed() {
    return this._checkToken().then(() => {
      return this._tokenParsed;
    });
  }
}
export { DrupalService as default };
//# sourceMappingURL=DrupalService.js.map
